import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Chip, IconButton, MenuItem, Tabs, Tab, Alert,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import SerialNoDataGrid from '../../components/ui/SerialNoDataGrid';
import { Add, Edit, Delete, Sync } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import {
  listMasterGroups, saveMasterGroup, deleteMasterGroup, syncMasterGroups,
  type MasterDefinitionGroup,
} from '../../services/platform/runtimeService';

const ICON_OPTIONS = ['Category', 'Public', 'DirectionsCar', 'Restaurant', 'Build', 'Payments', 'Badge'];

export default function MasterBuilderPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState<MasterDefinitionGroup | null>(null);
  const [form, setForm] = useState<Partial<MasterDefinitionGroup>>({
    key: '', label: '', description: '', icon: 'Category', isCommon: true, types: [], sortOrder: 100,
  });
  const [typeDraft, setTypeDraft] = useState({ key: '', label: '', description: '', module: '' });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['master-groups'],
    queryFn: listMasterGroups,
  });

  const saveMutation = useMutation({
    mutationFn: () => saveMasterGroup(form, editing?.key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['master-groups'] });
      queryClient.invalidateQueries({ queryKey: ['master-catalog'] });
      setDialogOpen(false);
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => deleteMasterGroup(key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['master-groups'] }),
  });

  const syncMutation = useMutation({
    mutationFn: syncMasterGroups,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['master-groups'] }),
  });

  const columns: GridColDef[] = [
    { field: 'key', headerName: 'Key', width: 140 },
    { field: 'label', headerName: 'Group', flex: 1, minWidth: 160 },
    { field: 'module', headerName: 'Service', width: 130 },
    { field: 'types', headerName: 'Types', width: 90, valueGetter: (v) => (v as unknown[])?.length || 0 },
    {
      field: 'isCommon', headerName: 'Scope', width: 100,
      renderCell: (p) => <Chip label={p.value ? 'Common' : 'Service'} size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 100, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={() => { setEditing(p.row as MasterDefinitionGroup); setForm(p.row as MasterDefinitionGroup); setDialogOpen(true); }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => deleteMutation.mutate((p.row as MasterDefinitionGroup).key)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        gradient
        eyebrow="Metadata Engine"
        title="Master Builder"
        subtitle="Create and edit master groups and types — no code deploy required."
        actions={
          <>
            <Button variant="outlined" color="inherit" startIcon={<Sync />} onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              Sync from Seed
            </Button>
            <Button variant="contained" color="inherit" startIcon={<Add />} onClick={() => {
              setEditing(null);
              setForm({ key: '', label: '', description: '', icon: 'Category', isCommon: true, types: [], sortOrder: 100 });
              setDialogOpen(true);
            }}>
              New Group
            </Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>Masters defined here drive Master Data UI, APIs, and service-specific catalogs at runtime.</Alert>

      <SerialNoDataGrid rows={groups} columns={columns} loading={isLoading} getRowId={(r) => r.key} autoHeight
        sx={{ bgcolor: 'background.paper', borderRadius: 3 }} />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.label}` : 'Create Master Group'}</DialogTitle>
        <DialogContent dividers>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Group" /><Tab label="Types" />
          </Tabs>
          {tab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField fullWidth label="Group Key" value={form.key || ''} disabled={!!editing} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Label" value={form.label || ''} onChange={(e) => setForm({ ...form, label: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
              <Grid item xs={4}>
                <TextField select fullWidth label="Icon" value={form.icon || 'Category'} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
                  {ICON_OPTIONS.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4}><TextField fullWidth label="Service Module" value={form.module || ''} onChange={(e) => setForm({ ...form, module: e.target.value, isCommon: !e.target.value })} /></Grid>
              <Grid item xs={4}><TextField fullWidth type="number" label="Sort Order" value={form.sortOrder ?? 0} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} /></Grid>
            </Grid>
          )}
          {tab === 1 && (
            <Box>
              <Grid container spacing={1} mb={2}>
                <Grid item xs={3}><TextField size="small" fullWidth label="Type Key" value={typeDraft.key} onChange={(e) => setTypeDraft({ ...typeDraft, key: e.target.value })} /></Grid>
                <Grid item xs={3}><TextField size="small" fullWidth label="Label" value={typeDraft.label} onChange={(e) => setTypeDraft({ ...typeDraft, label: e.target.value })} /></Grid>
                <Grid item xs={3}><TextField size="small" fullWidth label="Module" value={typeDraft.module} onChange={(e) => setTypeDraft({ ...typeDraft, module: e.target.value })} /></Grid>
                <Grid item xs={3}>
                  <Button fullWidth variant="outlined" sx={{ height: 40 }} onClick={() => {
                    if (!typeDraft.key || !typeDraft.label) return;
                    setForm({ ...form, types: [...(form.types || []), { ...typeDraft }] });
                    setTypeDraft({ key: '', label: '', description: '', module: '' });
                  }}>Add Type</Button>
                </Grid>
              </Grid>
              {(form.types || []).map((t, idx) => (
                <Chip key={idx} label={`${t.label} (${t.key})`} sx={{ m: 0.5 }}
                  onDelete={() => setForm({ ...form, types: (form.types || []).filter((_, i) => i !== idx) })} />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.key || !form.label}>Save</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
